import React, { useEffect, useState } from 'react';
import { firestore } from '../firebase';
import AddBranch from './AddBranch';
import { PencilSquareIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import PermissionGate from './PermissionGate';

function Branches() {
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null); // Holds the branch to edit
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state
  const [searchQuery, setSearchQuery] = useState(''); // Search query
  const [debouncedTerm, setDebouncedTerm] = useState(searchQuery);

  const getUniqueBranchFieldOptions = (fieldName) => {
    const uniqueValues = new Set();

    branches.forEach((branch) => {
      const values = Array.isArray(branch[fieldName]) ? branch[fieldName] : [];
      values
        .filter((value) => typeof value === 'string' && value.trim())
        .forEach((value) => uniqueValues.add(value.trim()));
    });

    return Array.from(uniqueValues).sort((a, b) => a.localeCompare(b));
  };

  const featureOptions = getUniqueBranchFieldOptions('features');
  const seatingOptions = getUniqueBranchFieldOptions('seating');

  // Debounce effect for search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Filter branches based on search
  useEffect(() => {
    let temp = branches;

    if (debouncedTerm) {
      temp = temp.filter((branch) => {
        const branchNameMatch = branch.branchName?.toLowerCase().includes(debouncedTerm.toLowerCase());
        const branchCodeMatch = branch.branchCode?.toLowerCase().includes(debouncedTerm.toLowerCase());
        const cityMatch = branch.city?.toLowerCase().includes(debouncedTerm.toLowerCase());
        return branchNameMatch || branchCodeMatch || cityMatch;
      });
    }

    setFilteredBranches(temp);
  }, [debouncedTerm, branches]);

  useEffect(() => {
    const unsubscribe = firestore.collection('branches').onSnapshot(
      snapshot => {
        console.log('Branches snapshot received:', snapshot.docs.length, 'documents');
        const branchList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Branch list:', branchList);
        setBranches(branchList);
        setFilteredBranches(branchList);
        setLoading(false); // Set loading to false once data is fetched
      },
      error => {
        console.error('Error fetching branches:', error);
        setAlertMessage(`Error loading branches: ${error.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleDelete = async (id, branchName) => {
    setConfirmMessage(`Are you sure you want to delete the branch "${branchName}"?`);
    setConfirmCallback(() => async () => {
      await firestore.collection('branches').doc(id).delete();
      setAlertMessage('Branch deleted successfully.');
      setConfirmMessage(null);
    });
  };

  const handleEdit = (branch) => {
    setCurrentBranch(branch); // Set current branch for editing
    setShowAdd(true);
  };

  // Helper function to generate image URL from branch name
  const getBranchImageUrl = (branchName) => {
    if (!branchName) return '';
    // Convert branch name to lowercase and replace spaces with hyphens
    return `https://cdn.shopify.com/s/files/1/0549/6268/4053/t/3/assets/${branchName}.webp`;
  };

  const getRazorpayAccountLabel = (account) => {
    if (account === 'thegot') return 'TheGot';
    if (account === 'muhavra') return 'Muhavra';
    return '-';
  };

  return (
    <div className='m-auto p-10 max-w-screen-xl'>
      {/* Alert Component */}
      {alertMessage && (
        <Alert
          message={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      {/* Confirm Component */}
      {confirmMessage && (
        <Confirm
          message={confirmMessage}
          onConfirm={confirmCallback}
          onCancel={() => setConfirmMessage(null)}
        />
      )}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Branches</h2>
        <PermissionGate permission="tv.branches.create"><button
          onClick={() => {
            setCurrentBranch(null); // Clear current branch to add a new one
            setShowAdd(true);
          }}
          className="bg-teal-500 text-white px-4 py-2 rounded"
        >
          Add Branch
        </button></PermissionGate>
      </div>

      {/* Search Bar */}
      <div className="flex items-center border-2 border-grey-500 rounded-lg px-3 py-2 w-full mb-4">
        <MagnifyingGlassIcon className="h-5 w-5 text-black font-bold" />
        <input
          className="ml-2 w-full outline-none bg-transparent"
          type="text"
          placeholder="Search by Branch Name, Branch Code, or City"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {showAdd && (
        <AddBranch
          onClose={() => setShowAdd(false)}
          currentBranch={currentBranch} // Pass current branch if in edit mode
          featureOptions={featureOptions}
          seatingOptions={seatingOptions}
        />
      )}

      {/* Loader */}
      {loading ? (
        <div className="text-center py-4">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className='mt-2'>Loading branches...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Image</th>
                <th className="py-2 px-4 border-b">Branch Name</th>
                <th className="py-2 px-4 border-b">Branch Code</th>
                <th className="py-2 px-4 border-b hidden lg:table-cell">Phone</th>
                <th className="py-2 px-4 border-b">Ordering</th>
                <th className="py-2 px-4 border-b hidden lg:table-cell">Razorpay</th>
                <th className="py-2 px-4 border-b hidden xl:table-cell">Seating</th>
                <th className="py-2 px-4 border-b hidden xl:table-cell">Features</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBranches.map((branch) => (
                <tr key={branch.id}>
                  <td className="py-2 px-4 border-b">
                    <img
                      src={getBranchImageUrl(branch.storeName || branch.branchName)}
                      alt={branch.branchName}
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </td>
                  <td className="py-2 px-4 border-b">{branch.branchName}</td>
                  <td className="py-2 px-4 border-b">{branch.branchCode}</td>
                  <td className="py-2 px-4 border-b hidden lg:table-cell">{branch.phone}</td>
                  <td className="py-2 px-4 border-b">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        branch.orderingEnabled === true
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {branch.orderingEnabled === true ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="py-2 px-4 border-b hidden lg:table-cell">
                    {getRazorpayAccountLabel(branch.razorpayAccount)}
                  </td>
                  <td className="py-2 px-4 border-b hidden xl:table-cell">
                    {Array.isArray(branch.seating) && branch.seating.length > 0 ? branch.seating.join(', ') : '-'}
                  </td>
                  <td className="py-2 px-4 border-b hidden xl:table-cell">
                    {Array.isArray(branch.features) && branch.features.length > 0 ? branch.features.join(', ') : '-'}
                  </td>
                  <td className="py-2 px-4 border-b">
                    <PermissionGate permission="tv.branches.update"><button
                      onClick={() => handleEdit(branch)}
                      className="text-teal-500 mr-2 hover:opacity-70"
                    >
                      <PencilSquareIcon className="size-8" />
                    </button></PermissionGate>
                    <PermissionGate permission="tv.branches.delete"><button
                      onClick={() => handleDelete(branch.id, branch.branchName)}
                      className="text-red-500 hover:opacity-70"
                    >
                      <TrashIcon className="size-8" />
                    </button></PermissionGate>
                  </td>
                </tr>
              ))}
              {filteredBranches.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No branches found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Branches;
